import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppContext } from '@/hooks'
import { useSpaceManagement } from '@/hooks/useSpaceManagement'
import { SpaceManagementTable, AreaEditDrawer } from '@/components/space-management'
import { UnitsMutateDrawer } from '@/components/units/UnitsMutateDrawer'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  SPACE_CATEGORY_TABS,
  type SpaceCategoryTab,
} from '@/types/space-management'

/**
 * Space Management page
 *
 * Unified view of units grouped by building (area) with
 * allocation counts, contract aircraft counts, and configurable columns.
 * Route: /:locationSlug/space
 */
export function SpaceManagementPage() {
  const { locationId, locationSlug } = useAppContext()
  const navigate = useNavigate()
  const { data: areas, isLoading } = useSpaceManagement(locationId ?? null)

  const [activeTab, setActiveTab] = useState<SpaceCategoryTab>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [editUnitId, setEditUnitId] = useState<string | null>(null)
  const [editAreaId, setEditAreaId] = useState<string | null>(null)

  // Filter areas by active category tab
  const filteredAreas = useMemo(() => {
    if (!areas) return []
    const tabDef = SPACE_CATEGORY_TABS.find((t) => t.value === activeTab)
    if (!tabDef || !tabDef.areaTypes) return areas // 'all' tab

    return areas.filter((area) => tabDef.areaTypes!.includes(area.type))
  }, [areas, activeTab])

  // Navigate to ramp view focused on a specific area/unit
  const handleViewOnMap = (unitId: string, areaId: string) => {
    navigate(`/${locationSlug}/ramp?area=${areaId}&unit=${unitId}`)
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          Space Management
        </h1>
        <p className="text-muted-foreground">
          Units grouped by building with occupancy and performance metrics
        </p>
      </div>

      {/* Category Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as SpaceCategoryTab)}
      >
        <TabsList>
          {SPACE_CATEGORY_TABS.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Grouped Table */}
      <SpaceManagementTable
        areas={filteredAreas}
        isLoading={isLoading}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onViewOnMap={handleViewOnMap}
        onEditUnit={setEditUnitId}
        onEditArea={setEditAreaId}
      />

      {/* Edit Unit Drawer */}
      <UnitsMutateDrawer
        open={!!editUnitId}
        onOpenChange={(open) => !open && setEditUnitId(null)}
        unitId={editUnitId ?? undefined}
      />

      {/* Edit Area Drawer */}
      <AreaEditDrawer
        open={!!editAreaId}
        onOpenChange={(open) => !open && setEditAreaId(null)}
        areaId={editAreaId ?? undefined}
      />
    </div>
  )
}
